import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Paper,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  AddAPhoto,
  MyLocation,
  CheckCircle,
  NavigateNext,
  NavigateBefore,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import exifr from 'exifr';
import { catService } from '../../services/api';
import 'leaflet/dist/leaflet.css';

// Fix marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const steps = ['Photo', 'Location', 'Details'];

const LocationPicker: React.FC<{
  position: [number, number] | null;
  setPosition: (pos: [number, number]) => void;
}> = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  return position ? <Marker position={position} /> : null;
};

const RecenterMap: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, 15);
  }, [center, map]);
  return null;
};

const UploadCat: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Photo state
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [exifLocation, setExifLocation] = useState<[number, number] | null>(null);

  // Location state
  const [manualLocation, setManualLocation] = useState<[number, number] | null>(null);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [useExifLocation, setUseExifLocation] = useState(true);

  // Details state
  const [name, setName] = useState('');
  const [breed, setBreed] = useState('');
  const [notes, setNotes] = useState('');
  const [likesPats, setLikesPats] = useState(false);

  useEffect(() => {
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation([pos.coords.latitude, pos.coords.longitude]);
        },
        () => {}
      );
    }
  }, []);

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhoto(file);
    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result as string);
    reader.readAsDataURL(file);

    // Extract EXIF GPS data
    try {
      const gps = await exifr.gps(file);
      if (gps && gps.latitude && gps.longitude) {
        setExifLocation([gps.latitude, gps.longitude]);
        setUseExifLocation(true);
      } else {
        setExifLocation(null);
        setUseExifLocation(false);
      }
    } catch (err) {
      console.log('EXIF extraction failed:', err);
      setExifLocation(null);
      setUseExifLocation(false);
    }
  };

  const handleUseMyLocation = () => {
    if (userLocation) {
      setManualLocation(userLocation);
    }
  };

  const getFinalLocation = (): [number, number] | null => {
    if (useExifLocation && exifLocation) return exifLocation;
    if (manualLocation) return manualLocation;
    return null;
  };

  const handleSubmit = async () => {
    if (!photo || !name.trim()) {
      setError('Photo and name are required');
      return;
    }

    const location = getFinalLocation();
    if (!location) {
      setError('Please select a location');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('photo', photo);
      formData.append('name', name);
      formData.append('breed', breed);
      formData.append('notes', notes);
      formData.append('likes_pats', String(likesPats));
      formData.append('manualLatitude', String(location[0]));
      formData.append('manualLongitude', String(location[1]));

      const cat = await catService.create(formData);
      navigate(`/cats/${cat._id}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload cat');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return !!photo;
      case 1:
        return !!getFinalLocation();
      case 2:
        return !!name.trim();
      default:
        return false;
    }
  };

  const defaultMapCenter: [number, number] = userLocation || [37.7749, -122.4194];

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        py: 4,
        background: 'linear-gradient(180deg, #0f0f1a 0%, #1a1a2e 100%)',
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant="h3"
          fontWeight="bold"
          sx={{
            mb: 1,
            background: 'linear-gradient(135deg, #f1f5f9 0%, #94a3b8 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Upload a Cat
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Share a cat you've spotted with the community
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 4 }}>
          {/* Step 1: Photo */}
          {activeStep === 0 && (
            <Box sx={{ textAlign: 'center' }}>
              {photoPreview ? (
                <Box sx={{ mb: 3 }}>
                  <Box
                    component="img"
                    src={photoPreview}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 400,
                      borderRadius: 2,
                      mb: 2,
                    }}
                  />
                  {exifLocation && (
                    <Alert severity="success" icon={<CheckCircle />}>
                      Location data found in photo!
                    </Alert>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    border: '2px dashed rgba(255,255,255,0.2)',
                    borderRadius: 3,
                    p: 6,
                    mb: 3,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: 'rgba(124, 58, 237, 0.05)',
                    },
                  }}
                >
                  <AddAPhoto sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Select a photo of a cat
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    If the photo has GPS data, we'll use it automatically
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                component="label"
                startIcon={<AddAPhoto />}
                size="large"
              >
                {photo ? 'Change Photo' : 'Choose Photo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handlePhotoSelect}
                />
              </Button>
            </Box>
          )}

          {/* Step 2: Location */}
          {activeStep === 1 && (
            <Box>
              {exifLocation && (
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useExifLocation}
                        onChange={(e) => setUseExifLocation(e.target.checked)}
                      />
                    }
                    label="Use location from photo"
                  />
                  {useExifLocation && (
                    <Typography variant="body2" color="success.main">
                      Using coordinates from photo: {exifLocation[0].toFixed(6)},{' '}
                      {exifLocation[1].toFixed(6)}
                    </Typography>
                  )}
                </Box>
              )}

              {(!exifLocation || !useExifLocation) && (
                <>
                  <Typography variant="body1" gutterBottom>
                    Click on the map to set the cat's location
                  </Typography>
                  <Button
                    startIcon={<MyLocation />}
                    onClick={handleUseMyLocation}
                    disabled={!userLocation}
                    sx={{ mb: 2 }}
                  >
                    Use My Location
                  </Button>
                  <Box
                    sx={{
                      height: 400,
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <MapContainer
                      center={manualLocation || defaultMapCenter}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        className="catlas-map-tiles--dark"
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      />
                      <LocationPicker
                        position={manualLocation}
                        setPosition={setManualLocation}
                      />
                      {manualLocation && <RecenterMap center={manualLocation} />}
                    </MapContainer>
                  </Box>
                  {manualLocation && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Selected: {manualLocation[0].toFixed(6)}, {manualLocation[1].toFixed(6)}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Step 3: Details */}
          {activeStep === 2 && (
            <Box>
              <TextField
                label="Cat's Name"
                fullWidth
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ mb: 3 }}
                placeholder="e.g., Whiskers, Orange Tabby, etc."
              />
              <TextField
                label="Breed (optional)"
                fullWidth
                value={breed}
                onChange={(e) => setBreed(e.target.value)}
                sx={{ mb: 3 }}
                placeholder="e.g., Tabby, Siamese, Maine Coon, etc."
              />
              <TextField
                label="Notes (optional)"
                fullWidth
                multiline
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mb: 3 }}
                placeholder="Any additional info about this cat..."
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={likesPats}
                    onChange={(e) => setLikesPats(e.target.checked)}
                  />
                }
                label="This cat likes pats"
              />
            </Box>
          )}

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={() => setActiveStep((prev) => prev - 1)}
              disabled={activeStep === 0}
              startIcon={<NavigateBefore />}
            >
              Back
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!canProceed() || loading}
                endIcon={loading ? <CircularProgress size={20} /> : <CheckCircle />}
              >
                {loading ? 'Uploading...' : 'Upload Cat'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={() => setActiveStep((prev) => prev + 1)}
                disabled={!canProceed()}
                endIcon={<NavigateNext />}
              >
                Next
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default UploadCat;
